import { Button } from "@loot-os/ui/components/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Copy } from "lucide-react";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";
import { useStandingNotice } from "../lib/useStandingNotice";

export function EmbedPage() {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const trpc = useTRPC();
  const navigate = useNavigate();
  const keys = useQuery(trpc.apiKeys.list.queryOptions());
  const boxes = useQuery(trpc.boxes.list.queryOptions());
  const [copied, setCopied] = useState(false);

  const publishableKey = keys.data?.find((k) => k.type === "publishable" && !k.revokedAt);
  const box = boxes.data?.find((b) => b.status === "active") ?? boxes.data?.[0];

  // The snippet below renders either way, with placeholders; these say why.
  useStandingNotice(keys.isSuccess && !publishableKey, {
    id: "embed-no-key",
    message: "You have no active publishable key — create one and substitute it below.",
    action: {
      label: "Create key",
      onClick: () => void navigate({ to: "/tenants/$tenantId/keys", params: { tenantId } }),
    },
  });
  useStandingNotice(boxes.data?.length === 0, {
    id: "embed-no-boxes",
    message: "You have no boxes yet — create and publish one so the opener has something to open.",
    action: {
      label: "Go to boxes",
      onClick: () => void navigate({ to: "/tenants/$tenantId/boxes", params: { tenantId } }),
    },
  });

  const keyPlaceholder = publishableKey
    ? `${publishableKey.prefix}…` // only the prefix is stored — paste your full key
    : "los_test_pk_YOUR_PUBLISHABLE_KEY";
  const boxId = box?.id ?? "YOUR_BOX_ID";

  const snippet = `import { LootBoxOpener } from "@loot-os/react";
import "@loot-os/react/styles.css";

export function Storefront({ sessionToken }: { sessionToken?: string }) {
  return (
    <LootBoxOpener
      boxId="${boxId}"
      publishableKey="${keyPlaceholder}"
      // Mint session tokens server-side with your SECRET key:
      //   POST /v1/users/{externalId}/sessions
      // Omit sessionToken for demo mode (local sampling, nothing persists).
      sessionToken={sessionToken}
      onOpened={(result) => console.log("opened", result)}
      onSessionExpired={() => {
        // fetch a fresh session token from your backend
      }}
    />
  );
}`;

  const copy = async () => {
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Embed</h1>
        <p className="text-sm text-muted-foreground">
          Drop the Loot OS opener into your site with a publishable key.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>React</CardTitle>
          <CardAction className="flex items-center gap-3">
            <code className="font-mono text-xs text-muted-foreground">pnpm add @loot-os/react</code>
            <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
              <Copy />
              {copied ? "Copied!" : "Copy snippet"}
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <pre className="overflow-x-auto rounded-md border bg-muted/50 p-4 font-mono text-xs leading-relaxed">
            {snippet}
          </pre>
          {publishableKey && (
            <p className="text-sm text-muted-foreground">
              <code className="font-mono text-xs">{publishableKey.prefix}…</code> is your key's
              display prefix — Loot OS stores only a hash of the full key, so paste the plaintext
              you copied when it was created.
              {box ? (
                <>
                  {" "}
                  The box id belongs to <strong className="text-foreground">{box.name}</strong>
                  {box.status !== "active" && " (not active yet — activate it before going live)"}.
                </>
              ) : null}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How it fits together</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>
              Your server creates end users and credits their balances via the public API using a{" "}
              <strong className="text-foreground">secret</strong> key.
            </li>
            <li>
              Your server mints a short-lived{" "}
              <strong className="text-foreground">session token</strong> per signed-in user.
            </li>
            <li>
              The browser renders <code className="font-mono text-xs">&lt;LootBoxOpener /&gt;</code>{" "}
              with your <strong className="text-foreground">publishable</strong> key + that session
              token; opens are debited atomically and are provably fair.
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
